/**
 * Shared Tax Calculation Module
 * 
 * This module provides pure calculation functions for Indonesian PPh 21 progressive tax
 * with year-to-date (YTD) calculation support. It can be used in both client-side
 * (React) and server-side (Supabase Edge Functions) contexts.
 * 
 * Key Features:
 * - Progressive tax bracket calculation
 * - Year-to-date tax accumulation
 * - Indonesian tax rounding rules (PKP down to 1000 IDR, final tax to nearest rupiah)
 * - Service charge exclusion from taxable base
 * - Tax bracket validation
 */

/**
 * Tax bracket tier for progressive taxation
 * Uses currency amounts (IDR), not hours like overtime
 */
export interface TaxBracketTier {
  lower_bound: number;      // IDR, inclusive (e.g., 0, 60000000)
  upper_bound: number | null; // IDR, exclusive (null = unlimited)
  rate_percent: number;     // 0-100 (e.g., 5, 15, 25, 30, 35)
}

/**
 * Tax bracket configuration stored in JSONB
 */
export interface TaxBracketConfig {
  mode: 'progressive' | 'flat';
  tiers: TaxBracketTier[];
  calculation_basis: 'ytd_annual'; // Future: 'per_period', 'monthly'
  fiscal_year_start_month?: number; // 1-12, default 1 (January). Determines YTD accumulation boundary.
  rounding_rules?: {
    pkp_round_down_to?: number;     // Default: 1000 (IDR)
    tax_round_to_nearest?: number;  // Default: 1 (rupiah)
  };
}

/**
 * Breakdown of tax calculation per tier
 */
export interface TaxCalculationBreakdownItem {
  tierIndex: number;
  lower_bound: number;
  upper_bound: number | null;
  rate_percent: number;
  taxable_portion: number;
  tax_amount: number;
}

/**
 * Complete tax calculation result
 */
export interface TaxCalculationResult {
  rule_id: string;
  rule_name: string;
  ytd_taxable_income: number;      // Before rounding
  rounded_pkp: number;              // After PKP rounding
  annual_tax_gross: number;         // Before rounding
  annual_tax_rounded: number;       // After final rounding
  ytd_tax_withheld: number;         // Already paid this year
  current_period_withholding: number; // To withhold now
  breakdown: TaxCalculationBreakdownItem[];
  calculation_notes?: string;
}

/**
 * Components that make up taxable income
 */
export interface TaxableComponents {
  regular: number;
  overtime: number;
  holiday: number;
  allowances: number;
  bonuses: number;
}

/**
 * Extended calculation result with tax fields
 */
export interface ExtendedCompensationCalculationResult {
  totalGrossPay: number;
  totalEarnings: number;
  totalDeductions: number;
  netPay: number;
  taxableEarnings: number;
  taxSummary?: TaxCalculationResult;
  serviceChargeApplied: number;
  postTaxNetBeforeServiceCharge: number;
  lineItems: any[];
  calculationSummary: any;
}

/**
 * Safely convert any value to a number, handling Supabase numeric strings
 * and preventing NaN propagation
 * 
 * @param value - Value to convert (can be string, number, null, undefined)
 * @param defaultValue - Fallback value if conversion fails (default: 0)
 * @returns Safe number value
 */
export function toSafeNumber(value: any, defaultValue: number = 0): number {
  // Handle null/undefined
  if (value === null || value === undefined) {
    return defaultValue;
  }
  
  // Handle empty strings
  if (typeof value === 'string' && value.trim() === '') {
    return defaultValue;
  }
  
  // Convert to number
  const num = Number(value);
  
  // Check for NaN
  if (isNaN(num) || !isFinite(num)) {
    console.warn(`[toSafeNumber] Invalid numeric value: ${value}, using default: ${defaultValue}`);
    return defaultValue;
  }
  
  return num;
}

/**
 * Calculate tax using Indonesian progressive brackets with YTD
 * 
 * @param config Tax bracket configuration
 * @param currentPeriodTaxable Taxable earnings for this pay period
 * @param ytdTaxableEarnings Year-to-date taxable earnings (before this period)
 * @param ytdTaxWithheld Year-to-date tax already withheld (before this period)
 * @param ruleId Rule ID for tracking
 * @param ruleName Rule name for display
 * @returns Complete tax calculation result
 */
export function calculateProgressiveTax(
  config: TaxBracketConfig,
  currentPeriodTaxable: number,
  ytdTaxableEarnings: number,
  ytdTaxWithheld: number,
  ruleId: string = '',
  ruleName: string = 'Income Tax (PPh 21)'
): TaxCalculationResult {
  // ========== INPUT VALIDATION ==========
  
  // 1. Validate config structure
  if (!config || typeof config !== 'object') {
    throw new Error(`[TAX] Invalid config: must be an object`);
  }
  
  if (!config.tiers || !Array.isArray(config.tiers) || config.tiers.length === 0) {
    throw new Error(`[TAX] Invalid config: tiers must be a non-empty array`);
  }
  
  // 2. Normalize all numeric inputs using toSafeNumber
  const safeCurrent = toSafeNumber(currentPeriodTaxable, 0);
  const safeYTDEarnings = toSafeNumber(ytdTaxableEarnings, 0);
  const safeYTDWithheld = toSafeNumber(ytdTaxWithheld, 0);
  
  // 3. Validate that normalized values are non-negative
  if (safeCurrent < 0) {
    console.warn(`[TAX] Negative current period taxable: ${currentPeriodTaxable}, treating as 0`);
  }
  
  if (safeYTDEarnings < 0) {
    console.warn(`[TAX] Negative YTD earnings: ${ytdTaxableEarnings}, treating as 0`);
  }
  
  if (safeYTDWithheld < 0) {
    console.warn(`[TAX] Negative YTD withheld: ${ytdTaxWithheld}, treating as 0`);
  }
  
  // 4. Use normalized values for calculation
  const projectedAnnualTaxable = safeYTDEarnings + safeCurrent;
  
  // 2. Round PKP down to nearest 1000 IDR (Indonesian rule)
  const roundingFactor = config.rounding_rules?.pkp_round_down_to ?? 1000;
  const roundedPKP = Math.floor(projectedAnnualTaxable / roundingFactor) * roundingFactor;
  
  // 3. Apply progressive brackets
  let remainingIncome = roundedPKP;
  let totalTax = 0;
  const breakdown: TaxCalculationBreakdownItem[] = [];
  
  // Sort tiers by lower bound to ensure correct order
  const sortedTiers = [...config.tiers].sort((a, b) => a.lower_bound - b.lower_bound);
  
  for (let i = 0; i < sortedTiers.length; i++) {
    const tier = sortedTiers[i];
    
    // Normalize tier values to prevent NaN
    const lowerBound = toSafeNumber(tier.lower_bound, 0);
    const upperBound = tier.upper_bound === null ? null : toSafeNumber(tier.upper_bound);
    const ratePercent = toSafeNumber(tier.rate_percent, 0);
    
    // Validate rate is in valid range
    if (ratePercent < 0 || ratePercent > 100) {
      console.warn(`[TAX] Invalid tier ${i} rate: ${tier.rate_percent}%, skipping tier`);
      continue;
    }
    
    // Skip if no income left to tax
    if (remainingIncome <= lowerBound) {
      continue;
    }
    
    const tierStart = lowerBound;
    const tierEnd = upperBound ?? Infinity;
    
    // Calculate taxable amount in this tier
    const incomeInTier = Math.min(remainingIncome, tierEnd) - tierStart;
    const taxableInTier = Math.max(0, incomeInTier);
    
    if (taxableInTier > 0) {
      const tierTax = taxableInTier * (ratePercent / 100);
      totalTax += tierTax;
      
      breakdown.push({
        tierIndex: i,
        lower_bound: lowerBound,
        upper_bound: upperBound,
        rate_percent: ratePercent,
        taxable_portion: taxableInTier,
        tax_amount: tierTax
      });
    }
  }
  
  // 4. Round final tax to nearest rupiah (Indonesian rule)
  const roundToNearest = config.rounding_rules?.tax_round_to_nearest ?? 1;
  const roundedTax = Math.round(totalTax / roundToNearest) * roundToNearest;
  
  // 5. Calculate withholding for this period
  // This is the incremental tax: annual tax minus what's already been withheld
  const currentPeriodWithholding = Math.max(0, roundedTax - safeYTDWithheld);
  
  return {
    rule_id: ruleId,
    rule_name: ruleName,
    ytd_taxable_income: projectedAnnualTaxable,
    rounded_pkp: roundedPKP,
    annual_tax_gross: totalTax,
    annual_tax_rounded: roundedTax,
    ytd_tax_withheld: safeYTDWithheld,
    current_period_withholding: currentPeriodWithholding,
    breakdown,
    calculation_notes: `YTD calculation with ${breakdown.length} bracket tiers applied`
  };
}

/**
 * Validate tax bracket configuration
 * 
 * @param config Tax bracket configuration to validate
 * @returns Array of error messages (empty if valid)
 */
export function validateTaxConfig(config: TaxBracketConfig | undefined): string[] {
  const errors: string[] = [];

  if (!config || !config.tiers || config.tiers.length === 0) {
    errors.push('At least one tax bracket tier is required');
    return errors;
  }
  
  const sortedTiers = [...config.tiers].sort((a, b) => a.lower_bound - b.lower_bound);
  
  for (let i = 0; i < sortedTiers.length; i++) {
    const tier = sortedTiers[i];
    
    // Validate bounds
    if (tier.lower_bound < 0) {
      errors.push(`Tier ${i + 1}: Lower bound must be >= 0`);
    }
    
    if (tier.upper_bound !== null && tier.upper_bound <= tier.lower_bound) {
      errors.push(`Tier ${i + 1}: Upper bound must be > lower bound`);
    }
    
    // Validate rate
    if (tier.rate_percent < 0 || tier.rate_percent > 100) {
      errors.push(`Tier ${i + 1}: Rate must be between 0 and 100`);
    }
    
    // Check for gaps/overlaps between tiers
    if (i > 0) {
      const prevTier = sortedTiers[i - 1];
      if (prevTier.upper_bound !== null && prevTier.upper_bound !== tier.lower_bound) {
        errors.push(`Gap or overlap between tier ${i} and ${i + 1}`);
      }
    }
  }
  
  // First tier should start at 0 for progressive tax
  if (sortedTiers[0].lower_bound !== 0) {
    errors.push('First tier should start at 0');
  }
  
  // Last tier should be open-ended for progressive
  if (config.mode === 'progressive') {
    const lastTier = sortedTiers[sortedTiers.length - 1];
    if (lastTier.upper_bound !== null) {
      errors.push('Last tier should have null upper_bound for unlimited');
    }
  }
  
  return errors;
}

/**
 * Resolve taxable earnings from components (exclude service charges)
 * Service charges are added post-tax and should not be included in taxable base
 * 
 * @param components Breakdown of earnings components
 * @returns Total taxable earnings
 */
export function resolveTaxableEarnings(components: TaxableComponents): number {
  return components.regular 
    + components.overtime 
    + components.holiday 
    + components.allowances 
    + components.bonuses;
}

/**
 * Format currency for display (IDR)
 * 
 * @param amount Amount in IDR
 * @returns Formatted string
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
}

/**
 * Get Indonesian PPh 21 example configuration (2024 rates)
 * This is a helper for UI to show example brackets
 */
export function getIndonesianPPh21Example(): TaxBracketConfig {
  return {
    mode: 'progressive',
    calculation_basis: 'ytd_annual',
    fiscal_year_start_month: 1, // January (calendar year)
    tiers: [
      { lower_bound: 0, upper_bound: 60000000, rate_percent: 5 },
      { lower_bound: 60000000, upper_bound: 250000000, rate_percent: 15 },
      { lower_bound: 250000000, upper_bound: 500000000, rate_percent: 25 },
      { lower_bound: 500000000, upper_bound: 5000000000, rate_percent: 30 },
      { lower_bound: 5000000000, upper_bound: null, rate_percent: 35 }
    ],
    rounding_rules: {
      pkp_round_down_to: 1000,
      tax_round_to_nearest: 1
    }
  };
}

/**
 * Helper to compute the fiscal year start date given a pay period date and fiscal month.
 * Handles cross-calendar-year boundaries (e.g., fiscal month=4 means Apr–Mar).
 * 
 * @param periodStart The pay period start date
 * @param fiscalYearStartMonth 1-12, defaults to 1 (January)
 * @returns ISO date string YYYY-MM-DD for the fiscal year start
 */
export function getFiscalYearStartDate(periodStart: Date, fiscalYearStartMonth: number = 1): string {
  const month = fiscalYearStartMonth >= 1 && fiscalYearStartMonth <= 12 ? fiscalYearStartMonth : 1;
  const periodMonth = periodStart.getMonth() + 1; // 1-indexed
  const periodYear = periodStart.getFullYear();
  
  // If the period's month is before the fiscal start month, fiscal year started last calendar year
  const fiscalYear = periodMonth < month ? periodYear - 1 : periodYear;
  
  return `${fiscalYear}-${String(month).padStart(2, '0')}-01`;
}
